import { request } from '@umijs/max';

const VISIBILITY_TO_PATH: Record<string, string> = {
  personal: 'personal',
  groups: 'groups',
  firm: 'firm',
  public: 'public',
};

export const getLibrary = async (
  search?: string,
  group?: string | undefined,
  practiceAreaIds?: string[],
  pageSize = 20,
) => {
  const path =
    group && VISIBILITY_TO_PATH[group]
      ? `/api/library/prompts/${VISIBILITY_TO_PATH[group]}`
      : `/api/library/prompts`;
  const params: Record<string, any> = {
    page: 1,
    // cap results for the type-ahead; fetching the whole library freezes the composer
    pageSize,
    search: encodeURIComponent(search || ''),
  };
  if (practiceAreaIds?.length) {
    params.practiceAreaIds = practiceAreaIds.join(',');
  }
  return request(path, { params });
};

export async function updateLibrary(
  id: string,
  data: {
    name?: string;
    purpose?: string;
    content?: string;
    favorite?: boolean;
    tags?: string[];
    notes?: string;
    allowSelfInstruction?: boolean;
    contextDocumentIds?: string[];
    practiceAreaIds?: string[];
  },
) {
  return request(`/api/library/prompts/${id}`, { data, method: 'PUT' });
}

export async function updatePublishedLibrary(
  id: string,
  data: {
    name?: string;
    purpose?: string;
    content?: string;
    tags?: string[];
    notes?: string;
    allowSelfInstruction?: boolean;
    contextDocumentIds?: string[];
    practiceAreaIds?: string[];
  },
) {
  return request(`/api/library/prompts/published/${id}`, { data, method: 'PUT' });
}

export async function addLibrary(data: {
  name?: string;
  purpose?: string;
  content: string;
  tags?: string[];
  notes?: string;
  allowSelfInstruction?: boolean;
  contextDocumentIds?: string[];
  practiceAreaIds?: string[];
}) {
  return request(`/api/library/prompts`, { data, method: 'POST' });
}

export async function publishLibrary(data: {
  promptId: string;
  visibility: string;
  groupIds?: string[];
}) {
  return request(`/api/library/prompts/publish`, { data, method: 'POST' });
}

export const removeLibrary = async (id: string) => {
  return request(`/api/library/prompts/${id}`, { method: 'DELETE' });
};

export const removePublicLibrary = async (id: string) => {
  return request(`/api/library/prompts/publish/${id}`, { method: 'DELETE' });
};

export const getTags = async () => {
  return request(`/api/library/tags`);
};

export const getPracticeAreas = async () => {
  return request(`/api/library/practice-areas`);
};

export const updateExample = async (id: string, data: any) => {
  return request(`/api/review/checklist/${id}`, { method: 'PUT', data });
};

export const getPromptPublishRequests = async () => {
  return request(`/api/library/prompts/publish-requests`);
};

export type PromptApprovalDecision = {
  revision: number;
  updates?: Parameters<typeof updatePublishedLibrary>[1];
};

export const approvePromptPublishRequest = async (
  id: string,
  decision?: PromptApprovalDecision,
) => {
  return request(`/api/library/prompts/publish-request/${id}/approve`, {
    method: 'POST',
    ...(decision ? { data: decision } : {}),
  });
};

export const rejectPromptPublishRequest = async (id: string, decision?: { revision: number }) => {
  return request(`/api/library/prompts/publish-request/${id}/deny`, {
    method: 'POST',
    ...(decision ? { data: decision } : {}),
  });
};
